import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { GET_ENVIO } from "../../../data/graphQL/queries/GestionAsistencial/getEnvio";
import { GET_ENVIO_JUSTIFICACIONES } from "../../../data/graphQL/queries/GestionAsistencial/getEnvioJustificaciones";
import { GET_ENVIO_INDICACIONES } from "../../../data/graphQL/queries/GestionAsistencial/getEnvioIndicacionActiva";
import { GET_ENVIO_INSUMOS } from "../../../data/graphQL/queries/GestionAsistencial/getEnvioInsumos";
import moment from "moment";
import { GET_CONTRATO_CONDICIONAL } from "../../../data/graphQL/queries/GestionAsistencial/getContratoCondicionalByIdPaciente";
import { GET_BLOQUEO_CLIENTE_SAP } from "../../../data/graphQL/queries/GestionAsistencial/getBloqueoClienteSAP";
import { ENVIO_ESTADO_PENDIENTE_ID } from "./useEnvioCreationContentInputs";
import toast from "react-hot-toast";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

export function useEnvioCreationContentForm({
  idEvaluacion,
  idOrdenPedido,
  isNew
}) {
  const [dataEnvio, setDataEnvio] = useState(null);
  const [isActualizarStockRequired, setIsActualizarStockRequired] = useState(true);
  const [insumosRows, setInsumosRows] = useState([]);
  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const [initialEnvioEstado, setInitialEnvioEstado] = useState("");
  const [bloqueoClienteSAP, setBloqueoClienteSAP] = useState(null);
  const [showJustificacionCondicional, setShowJustificacionCondicional] = useState(!isNew || false);
  const [showBum, setShowBum] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'fechaEntrega') {
      if (formValues.diasPedido > 0) {
        getEnvioIndicaciones({
          variables: {
            idOrdenPedido,
            idEvaluacion,
            fechaDesde: formValues.fechaEntrega,
            fechaHasta: moment(formValues.fechaEntrega).add(formValues.diasPedido, 'days').add(-1, 'days').format("YYYY-MM-DD")
          }
        });
        getEnvioInsumos({
          variables: {
            idOrdenPedido,
            idEvaluacion,
            fechaDesde: formValues.fechaEntrega,
            fechaHasta: moment(formValues.fechaEntrega).add(formValues.diasPedido, 'days').add(-1, 'days').format("YYYY-MM-DD")
          }
        })
          .then(() => {
            toast('Al cambiar la fecha de envío se reinició la lista de insumos. Revisar nuevamente.', {
              icon: <WarningRoundedIcon color='warning' />,
            });
          });
      }
    }

    if (name === 'diasPedido' && value > 0) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        getEnvioIndicaciones({
          variables: {
            idOrdenPedido,
            idEvaluacion,
            fechaDesde: formValues.fechaEntrega,
            fechaHasta: moment(formValues.fechaEntrega).add(value, 'days').add(-1, 'days').format("YYYY-MM-DD")
          }
        });
        getEnvioInsumos({
          variables: {
            idOrdenPedido,
            idEvaluacion,
            fechaDesde: formValues.fechaEntrega,
            fechaHasta: moment(formValues.fechaEntrega).add(value, 'days').add(-1, 'days').format("YYYY-MM-DD")
          }
        }).then(() => {
          toast('Al cambiar la cantidad de días se reinició la lista de insumos. Revisar nuevamente.', {
            icon: <WarningRoundedIcon color='warning' />,
          });
        });
      }, 700);
    }

    if (name === 'idDeposito') {
      setIsActualizarStockRequired(true);
      toast('Al cambiar el depósito se requiere actualizar el stock.', {
        icon: <WarningRoundedIcon color='warning' />,
      });
    }
  };

  const formatDate = (date) => {
    return moment(date).format("DD/MM/YYYY");
  };

  const [getContratoCondicional] = useLazyQuery(GET_CONTRATO_CONDICIONAL);

  const [
    getEnvioJustificaciones,
    { data: dataJustificacion, loading: loadingJustificaciones }] =
    useLazyQuery(GET_ENVIO_JUSTIFICACIONES, {
      fetchPolicy: 'network-only'
    });

  const [getEnvio, { data: dataEnvioLazy, loading: loadingEnvio }] = useLazyQuery(GET_ENVIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEnvioLazy) return;
    setDataEnvio({ envio: dataEnvioLazy?.envio });
  }, [dataEnvioLazy]);

  useEffect(() => {
    if (!loadingEnvio) {
      getEnvio({
        variables: {
          idOrdenPedido,
          idEvaluacion
        }
      });
    }
  }, []);

  const [getEnvioIndicaciones, { data: dataEnvioIndicaciones, loading: loadingIndicaciones }] = useLazyQuery(GET_ENVIO_INDICACIONES, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEnvioIndicaciones) return;
    setFormValues((prev) => ({
      ...prev,
      indicaciones: dataEnvioIndicaciones?.envioIndicacionActiva
    }));
  }, [dataEnvioIndicaciones]);

  const [getEnvioInsumos, { data: dataEnvioInsumos, loading: loadingInsumos }] = useLazyQuery(GET_ENVIO_INSUMOS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataEnvioInsumos) return;
    setInsumosRows(dataEnvioInsumos?.envioInsumos);
  }, [dataEnvioInsumos]);

  const [getBloqueoSAP, { data: dataBloqueoSAP }] = useLazyQuery(GET_BLOQUEO_CLIENTE_SAP, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataBloqueoSAP) return;
    setBloqueoClienteSAP(dataBloqueoSAP?.bloqueoClienteSAP);
  }, [dataBloqueoSAP]);

  useEffect(() => {
    const { envioJustificaciones } = dataJustificacion || {};

    if (!envioJustificaciones) return setShowJustificacionCondicional(false);

    if (!dataEnvio) return;
    const { envio: { cabecera } } = dataEnvio;
    setShowJustificacionCondicional(cabecera?.diasCO > cabecera?.diasFacturacion * 0.25);

    setShowBum(cabecera?.diasCO > cabecera?.maxDiasCO);

    setFormValues((prev) => ({
      ...prev,
      // justificacion comercial
      idEstadoComercial: envioJustificaciones?.idEstadoComercial,
      idMotivoJustificacionComercial: envioJustificaciones?.idMotivoJustificacionComercial,
      usuarioComercial: envioJustificaciones?.usuarioComercial,
      fechaComercial: envioJustificaciones?.fechaComercial,
      obsComercial: envioJustificaciones?.obsComercial,
      // justificacion médico
      idEstadoMedico: envioJustificaciones?.idEstadoMedico,
      idMotivoJustificacionMedico: envioJustificaciones?.idMotivoJustificacionMedico,
      usuarioMedico: envioJustificaciones?.usuarioMedico,
      fechaMedico: envioJustificaciones?.fechaMedico,
      obsMedico: envioJustificaciones?.obsMedico,
      // justificacion BUM
      idEstadoBUM: envioJustificaciones?.idEstadoBUM,
      idMotivoJustificacionBUM: envioJustificaciones?.idMotivoJustificacionBUM,
      usuarioBUM: envioJustificaciones?.usuarioBUM,
      fechaBUM: envioJustificaciones?.fechaBUM,
      obsBUM: envioJustificaciones?.obsBUM
    }));
  }, [dataJustificacion, dataEnvio]);

  useEffect(() => {
    const formatData = async () => {
      if (!dataEnvio) return;
      if (dataEnvio && !loadingEnvio) {
        const {
          envio: { cabecera, ubicacion, contrato, indicaciones, solicitudIndicacion }
        } = dataEnvio;

        const hasSolicitudIndicacion = solicitudIndicacion?.idIndicacionAprobacion !== 0;
        const fechaEvaluacion = `${formatDate(contrato.fechaDesde)} - ${formatDate(contrato.fechaHasta)}`;

        const initialEstado = cabecera.idEstadoEnvio || "";
        setInitialEnvioEstado(initialEstado);

        if (initialEstado != ENVIO_ESTADO_PENDIENTE_ID) {
          getEnvioJustificaciones({
            variables: {
              idOrdenPedido,
              idEvaluacion
            }
          });
        }

        const contratoCoRes = await getContratoCondicional({
          variables: {
            idPaciente: parseInt(cabecera.hC)
          }
        });

        const contratosCo = contratoCoRes?.data?.contratoCondicionalByIdPaciente;
        setFormValues((prev) => ({
          ...prev,
          // cabecera
          delegacion: cabecera.delegacion,
          paciente: cabecera.paciente,
          edad: cabecera.edad,
          hC: cabecera.hC,
          codSAPPaciente: cabecera.codSAPPaciente,
          cobertura: cabecera.cobertura,
          estadoAsistencial: cabecera.estadoAsistencial,
          motivoSuspension: cabecera.motivoSuspension,
          idDelegacion: cabecera.idDelegacion,
          idEstadoEnvio: cabecera.idEstadoEnvio || "",
          idDeposito: cabecera.idDeposito || "",
          diasPedido: cabecera.diasPedido || "",
          fechaEntrega: cabecera.fechaEntrega ? moment(cabecera.fechaEntrega).format('YYYY-MM-DD') : "",
          complejidadPlan: cabecera.complejidadPlan,
          idResultadoPresupuesto: cabecera.idResultadoPresupuesto,
          diasCO: cabecera.diasCO,
          maxDiasCO: cabecera.maxDiasCO,
          diasFacturacion: cabecera.diasFacturacion,
          contratosCo,
          // ubicacion
          direccion: ubicacion.direccion,
          provincia: ubicacion.provincia,
          localidad: ubicacion.localidad,
          codigoPostal: ubicacion.codigoPostal,
          pais: ubicacion.pais,
          telefono: ubicacion.telefono === "0" ? "" : ubicacion.telefono,
          observaciones: ubicacion.observaciones,
          //contrato
          fechaDesdeEvaluacion: contrato.fechaDesde,
          fechaHastaEvaluacion: contrato.fechaHasta,
          fechaEvaluacion: fechaEvaluacion,
          planCuidado: contrato.planCuidado,
          conexionesSemanales: contrato.conexionesSemanales,
          // indicaciones
          indicaciones: indicaciones,
          // solicitud
          idIndicacionSolicitud: hasSolicitudIndicacion ? solicitudIndicacion.idIndicacionAprobacion : 0,
          fechaDesdeSolicitud: hasSolicitudIndicacion ? solicitudIndicacion.fechaDesde : "",
          fechaHastaSolicitud: hasSolicitudIndicacion ? solicitudIndicacion.fechaHasta : "",
          solicitanteSolicitud: hasSolicitudIndicacion ? solicitudIndicacion.solicitante : "",
          fechaAltaSolicitud: hasSolicitudIndicacion ? solicitudIndicacion.fechaAlta : ""
        }));
      }
    };
    formatData();
  }, [dataEnvio, dataJustificacion]);

  useEffect(() => {
    if (!dataEnvio) return;

    getBloqueoSAP({
      variables: {
        codCliente: dataEnvio?.envio?.cabecera?.idCliente
      }
    });

    const insumos = dataEnvio?.envio?.insumos?.map(
      ({ __typename, cantidadAPedir, ...rest }) => ({
        ...rest,
        cantidadAPedir: cantidadAPedir || "0",
        saldo: "-",
        cod_Articulo: "-",
        stockDisponibleDeposito: "-",
        cantidadCuota: "-",
        cuotaActiva: "-"
      })
    );

    setInsumosRows(insumos);
  }, [dataEnvio]);

  return {
    initialEnvioEstado,
    formValues,
    bloqueoClienteSAP,
    showJustificacionCondicional,
    showBum,
    handleChange,
    insumosRows,
    setInsumosRows,
    data: dataEnvio,
    loading: loadingJustificaciones || loadingEnvio,
    isActualizarStockRequired,
    setIsActualizarStockRequired,
  };
}

const INITIAL_VALUES = {
  // cabecera
  delegacion: "",
  paciente: "",
  edad: "",
  hC: "",
  codSAPPaciente: "",
  cobertura: "",
  idDelegacion: "",
  idEstadoEnvio: "",
  idDeposito: "",
  diasPedido: "",
  fechaEntrega: "",
  idCliente: "",
  // justificacion
  // justificacion comercial
  idEstadoComercial: "",
  idMotivoJustificacionComercial: "",
  usuarioComercial: "",
  fechaComercial: "",
  obsComercial: "",
  // justificacion médico
  idEstadoMedico: "",
  idMotivoJustificacionMedico: "",
  usuarioMedico: "",
  fechaMedico: "",
  obsMedico: "",
  // justificacion BUM
  idEstadoBUM: "",
  idMotivoJustificacionBUM: "",
  usuarioBUM: "",
  fechaBUM: "",
  obsBUM: "",
  // ubicacion
  direccion: "",
  provincia: "",
  localidad: "",
  codigoPostal: "",
  pais: "",
  telefono: "",
  observaciones: "",
  //contrato
  fechaEvaluacion: "",
  planCuidado: "",
  conexionesSemanales: "",
  // indicacion
  indicaciones: [],
  // solicitud indicación
  idIndicacionSolicitud: 0,
  fechaDesdeSolicitud: "",
  fechaHastaSolicitud: "",
  solicitanteSolicitud: "",
  fechaAltaSolicitud: ""
};